import { Component, OnInit, OnDestroy } from '@angular/core';
import { map } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subscription } from 'rxjs';

import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ConfigService } from 'src/app/_services/config.service';
import { FlyOverService } from 'src/app/_services/fly-over.service';
import { LoaderService } from 'src/app/_services/loader.service';
import { RolesService } from 'src/app/_services/roles.service';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css']
})
export class NavbarComponent implements OnInit, OnDestroy {
  username$: Observable<string>;
  private isAllowed$: Observable<Map<string, boolean>>;

  username: string;
  role: string;
  private allowedRoutes: Map<string, boolean>;
  shouldDisableRole = false;

  showLoader: Subscription;
  loading = true;

  userTypeMap = [];

  private jwt: string;

  constructor(
    private authenticationService: AuthenticationService,
    private configService: ConfigService,
    private flyOverService: FlyOverService,
    private loaderService: LoaderService,
    private rolesService: RolesService,
    private snackBar: MatSnackBar
  ) {
    this.isAllowed$ = this.authenticationService.allowedRoutes$.pipe(
      map(routeList => {
        return routeList.reduce(
          (aMap, route) => aMap.set(route, true),
          new Map()
        );
      })
    );

    this.isAllowed$.subscribe(
      data => {
        this.allowedRoutes = data;
      },
      error => {
        console.error('error :', error);
      }
    );
  }

  ngOnInit(): void {
    this.authenticationService.jsonWebTokenFull$.subscribe(
      ({ payload, jwt }) => {
        const tokenLoad = payload;

        this.jwt = jwt || null;

        this.username = tokenLoad.username;
        this.role = tokenLoad.role;

        this.rolesService.getRoles().subscribe(
          roles => {
            this.userTypeMap = roles.filter(r => {
              if (tokenLoad.roles && tokenLoad.roles.includes(r.key)) {
                return r;
              }
            });

            this.shouldDisableRole = this.userTypeMap.length === 1;
            this.loading = false;
          },
          error => {
            this.loading = false;
            console.error('rolesService error', error);
          }
        );
      }
    );
  }

  hasSelectedRole(): boolean {
    return this.shouldDisableRole;
  }

  doFlyOver({ user, navigateUrl, jsonWebToken, isFlyOverUrl = true }) {
    if (user.passwordExpired) {
      window.location.href = navigateUrl;
      return;
    }

    if (isFlyOverUrl) {
      navigateUrl = `${navigateUrl}/${jsonWebToken}`;
    }

    window.top.location.href = navigateUrl;
  }

  roleChanged() {
    this.authenticationService
      .changeRole(this.role)
      .toPromise()
      .then(this.doFlyOver, error => {
        let message =
          'The service is currently unavailable, please try again in a few minutes.';
        if (typeof error === 'string') {
          message = error;
        }
        this.snackBar.open(message, 'Dismiss', {
          duration: 8000
        });
      });
  }

  ngOnDestroy() {
    this.showLoader && this.showLoader.unsubscribe();
  }

  mayShow$(routeList: string[]): boolean {
    let result = false;
    if (this.allowedRoutes) {
      result = routeList.some(route => this.allowedRoutes.get(route));
    }

    return result;
  }

  mayShowHome$(): boolean {
    if (this.allowedRoutes) {
      return (
        !this.allowedRoutes.get('admin-manage-users') &&
        !this.allowedRoutes.get('instruction') &&
        !this.allowedRoutes.get('fee-upload')
      );
    }
  }

  navigateHome(): void {
    this.authenticationService
      .getCurrentUser(this.username)
      .toPromise()
      .then(
        user =>
          this.flyOverService
            .getWealthToken({
              email: user.email,
              username: user.username,
              roles: user.roles,
              webId: user.webId,
              role: this.role
            })
            .toPromise(),
        error => {
          this.snackBar.open(
            'Could not retrieve profile details, please check connection and try again',
            'Dismiss',
            {
              duration: 8000
            }
          );
          return null;
        }
      )
      .then(
        tokenData => {
          if (!tokenData) {
            return null;
          }

          const flyOverUrl = this.configService.get('flyOverUrl');
          const redirectURL = `${flyOverUrl}/mwiApp.html#!/fly-over/${tokenData.wealthToken}/${this.jwt}`;

          window.top.location.href = redirectURL.replace('internal-', '');
        },
        error => {
          console.error('error', error);

          this.snackBar.open(
            'Please contact Client Services, there seems to be some missing details on your account.',
            'Dismiss',
            {
              duration: 8000
            }
          );
          return null;
        }
      );
  }

  logout(): void {
    this.authenticationService.logout();
  }

  getSelectedRole(): string {
    return this.userTypeMap.find(r => r.key === this.role).name;
  }
}
